import { InputText } from 'primereact/inputtext'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CommonContext } from '../common/context/commonContext'
import { ScrollPanel } from 'primereact/scrollpanel'
import { useLocation, useNavigate } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { LocationData, LocationsSearch, MoveGoodsData } from '../../types/inventory'
import { useMountEffect } from 'primereact/hooks'
import { classNames } from 'primereact/utils'
import InventoryMoveScanDialog from './InventoryMoveScanDialog'
import { includeComma } from '../../utils/inputText'
import { tokens } from 'locales/tokens'
import { useTranslation } from 'react-i18next'
import {
    translateInventoryMoveProg,
    translateInventoryMoveTyp,
} from '../../utils/translateCodeUtils'

const LocationScan = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { $http, $toast } = useContext(CommonContext)
    const { t } = useTranslation()
    const [lctSeq, setLctSeq] = useState<string>()
    const [locations, setLocations] = useState<LocationData[]>([])
    const [moveGoodsList, setMoveGoodsList] = useState<MoveGoodsData[]>([])
    const [moveGoods, setMoveGoods] = useState<MoveGoodsData>()
    const [inventoryMoveScanVisible, setInventoryMoveScanVisible] = useState<boolean>(false)
    const scanRef = useRef<HTMLInputElement | null>(null)
    const [params, setParams] = useState<LocationsSearch>({
        lctSeq: undefined,
        page: 1,
        pageSize: 10,
        sort: '',
        sortOrder: undefined,
        sortField: '',
    })

    useMountEffect(() => {
        if (!lctSeq) {
            setParams({
                ...params,
                lctSeq: lctSeq,
            })
        }
        findMoveGoodsList()
    })

    const findLocations = useCallback(
        async (params: LocationsSearch) => {

            const paddedParams = {
                ...params,
                lctSeq: params.lctSeq?.padStart(10, '0') ?? params.lctSeq,
            }

            const { total, list } = await $http().post('/inventory/find-locations', paddedParams)

            setLctSeq('')
            if (total < 1) {
                setLocations([])
                $toast({ title: t(tokens.inventory.location_inquiry), text: t(tokens.inventory.not_exists_location_code), icon: 'success' })
                scanRef.current?.focus()
                return
            }

            navigate('/location-detail', { state: { lctSeq: list[0].lctSeq } })
            // setLocations(list)
        },
        [$http],
    )
    const findMoveGoodsList = useCallback(async () => {
        const list = await $http().post('/inventory/find-move-Goods-list', {})
        if (isEmpty(list)) {
            setMoveGoodsList([])
            return
        } else {
            console.log('findMoveGoodsList', list)
            setMoveGoodsList(list)
        }
        setLctSeq('')
        scanRef.current?.focus()
    }, [$http])

    useEffect(() => {
        // console.log("params", JSON.stringify(params))
        if (!params.lctSeq) return
        findLocations(params)
    }, [params, findLocations])

    const onKeyDownSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            setParams({
                ...params,
                lctSeq,
            })
        }
    }

    const onClickDetail = (lctSeq: string) => {
        setLctSeq(lctSeq)
        navigate(`/location-detail`, { state: { lctSeq: lctSeq } })
    }

    const onInventoryMoveScanVisibleHide = async (isChange: boolean) => {
        //if (isChange) {
        //    await findMoveGoodsList()
        //}
        await findMoveGoodsList() // 재고이동 취소 처리시
        setInventoryMoveScanVisible(false)
    }
    const onClickMoveGoods = (goods: MoveGoodsData) => {
        setMoveGoods(goods)
        setInventoryMoveScanVisible(true)
    }

    const formatDate = (dateStr: string) => {
        if (dateStr.length !== 8) {
            return dateStr // 혹시라도 잘못된 형식이 들어오면 그대로 반환
        }
        return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`
    }

    return (
        <>
            <div className="grid pda-search">
                <div className="col-12 flex pb-0">
                    <InputText
                        ref={scanRef}
                        value={lctSeq}
                        className="p-inputtext-md w-full"
                        placeholder={t(tokens.common.enter_location_code_or_scan_barcode)}
                        onChange={(e) => setLctSeq(e.currentTarget.value)}
                        onKeyDown={(e) => onKeyDownSearch(e)}
                        autoFocus={true}
                    />
                </div>
            </div>
            <div className="grid mt-0 pt-0 pl-1">
                {isEmpty(locations) ? (
                    <div className="col-12 h-full p-align-center vertical-align-middle">{/* 로케이션을 스캔해 주세요. */}</div>
                ) : (
                    <ScrollPanel className="flex w-full">
                        {locations.map((item) => (
                            <div className="mt-1 border-1 border-round-2xl bg-white" key={item.lctSeq} onClick={() => onClickDetail(item.lctSeq)}>
                                <div className="col-12 justify-content-between flex">
                                    <div className="font-bold rdms-text-deepblue">{t(tokens.inventory.location)}</div>
                                    <div className="font-bold">
                                        {item.lctTypNm} ({item.goodsTmprNm}/{item.lctSttNm}) : {item.lctNo}
                                    </div>
                                </div>
                                <div className="col-12 justify-content-between flex">
                                    <div className="font-bold">
                                        {item.areaNm} &gt; {item.sectorNm} &gt; {item.bayId} &gt; {item.rackRowId}
                                        {t(tokens.common.row)}
                                        {item.rackColId}
                                        {t(tokens.common.layer)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </ScrollPanel>
                )}
            </div>
            <div className="m-0 p-0 flex flex-wrap col-12">
                {moveGoodsList &&
                    moveGoodsList.map((moveGoods) => (
                        <div
                            className="col-12 flex flex-wrap card p-0 m-0 mb-1"
                            key={moveGoods.invnMvSeq}
                            onClick={() => onClickMoveGoods(moveGoods)}
                        >
                            <div className="col-12 flex flex-wrap p-0 px-1 align-items-center">
                                <div className="text-left col-6 p-2 pl-1 m-0">
                                    <span
                                        className={classNames('font-bold text-xl pl-1 pt-0 pb-0 m-0', {
                                            'text-color-blue': moveGoods.invnMvTypCd === 'C',
                                            'text-color-green': moveGoods.invnMvTypCd === 'M',
                                        })}
                                    >
                                        {translateInventoryMoveTyp(moveGoods.invnMvTypCd, t)}
                                    </span>
                                </div>
                                <div className="col-6 flex p-2 m-0 justify-content-end align-items-center">
                                    <span className="text-xl font-bold">
                                        {includeComma(moveGoods.mvGoodsQty, '-')} {moveGoods.gdslsUnitCd}
                                    </span>
                                </div>
                            </div>
                            <div className="col-12 flex flex-wrap p-0 px-1 align-items-center">
                                <span className="font-bold pl-2">{moveGoods.goodsNm}</span>
                            </div>
                            <div className="col-12 flex flex-wrap p-0 px-1 align-items-center" style={{ justifyContent: 'space-between' }}>
                                <span className="pl-2 pt-1">{moveGoods.specTxt}</span>
                                <span className="font-bold mr-1">
                                    {t(tokens.common.expiration_date)}: {formatDate(moveGoods.shlfYmd)}
                                </span>
                            </div>
                            <div className="col-12 flex flex-wrap p-0 px-1 align-items-center"  style={{ justifyContent: 'flex-end' }}>
                                <span className="font-bold mr-1">
                                    {t(tokens.common.lot_no)}: {moveGoods.lotNo}
                                </span>
                            </div>
                            <div className="col-12 flex align-items-center pl-0 py-2 pt-1 pr-1 title-divider">
                                {moveGoods.invnMvTypCd === 'C' ? (
                                    <>
                                        <div className="col-5 flex align-items-center justify-content-start p-0 pl-1">
                                            <span className="text-color-blue font-bold pl-1 mr-2">{t(tokens.common.departure)} : </span>
                                            <span className="font-bold">{moveGoods.dprCenterNm}</span>
                                        </div>
                                        <div className="col-5 flex align-items-center justify-content-start p-0 pl-1">
                                            <span className="text-color-blue font-bold pl-1 mr-2">{t(tokens.common.arrival)} :</span>
                                            <span className="font-bold">{moveGoods.arvCenterNm}</span>
                                        </div>
                                        <div className="col-2 flex align-items-center p-0">
                                            <h5 className="text-color-red p-0 m-0">{moveGoods.invnMvProgNm}</h5>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="col-2 flex p-0 justify-content-end align-content-center">
                                            <span className="text-color-blue font-bold pl-1 mr-2">{t(tokens.common.departure)} :</span>
                                        </div>
                                        <div className="col-8 flex p-0">
                                            <span className="text-sm">{moveGoods.srcLctPathNm} [{moveGoods.locationAlias}]</span>
                                        </div>
                                        <div className="col-2 flex p-0">
                                            <h5 className="text-color-red p-0 m-0">
                                                {translateInventoryMoveProg(moveGoods.invnMvProgCd, t)}
                                            </h5>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
            </div>
            {inventoryMoveScanVisible && (
                <InventoryMoveScanDialog visible={inventoryMoveScanVisible} onHide={(e) => onInventoryMoveScanVisibleHide(e)} data={moveGoods} />
            )}
        </>
    )
}

export default LocationScan
